import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner, Button, Card, Badge, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { 
    IconMail, 
    IconPhone, 
    IconShield, 
    IconCalendar, 
    IconBuildingStore, 
    IconFileAnalytics, 
    IconLock, 
    IconCircleCheck, 
    IconClock, 
    IconAlertCircle,
    IconCamera,
    IconTrash,
    IconMoodSmile
} from '@tabler/icons-react';
import { fetchUserStats } from 'api/formApi';
import { authApi } from 'api/authApi';
import { userApi } from 'api/userApi';
import Avatar from 'components/general/Avatar';
import AvatarPickerModal from 'components/profile/AvatarPickerModal';
import './Profile.scss';

const Profile = () => {
    const dispatch = useDispatch();
    const { user: { user } } = useSelector((state) => state.auth);
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);
    const [resetLoading, setResetLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [showAvatarModal, setShowAvatarModal] = useState(false);

    const handleSelectAvatarUrl = async (avatarUrl) => {
        const userId = user?.id || user?._id;
        if (!userId) {
            toast.error('User ID not found.');
            return;
        }

        setUploadingImage(true);
        try {
            const response = await userApi.updateUser({
                userId,
                values: { profileImage: avatarUrl },
            });

            if (response?.status === 200 || response?.data) {
                dispatch({ type: 'UPDATE_USER_PROFILE_IMAGE', payload: avatarUrl });
                toast.success('Profile picture updated successfully!');
                setShowAvatarModal(false);
            } else {
                toast.error('Failed to update profile picture.');
            }
        } catch (error) {
            toast.error('An error occurred while updating profile picture.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveProfileImage = async () => {
        const userId = user?.id || user?._id;
        if (!userId) return;

        setUploadingImage(true);
        try {
            const response = await userApi.updateUser({
                userId,
                values: { profileImage: '' },
            });
            if (response?.status === 200 || response?.data) {
                dispatch({ type: 'UPDATE_USER_PROFILE_IMAGE', payload: '' });
                toast.success('Profile picture removed.');
                setShowAvatarModal(false);
            } else {
                toast.error('Failed to remove profile picture.');
            }
        } catch (error) {
            toast.error('An error occurred while removing profile picture.');
        } finally {
            setUploadingImage(false);
        }
    };

    useEffect(() => {
        const getStats = async () => {
            try {
                const response = await fetchUserStats();
                if (response?.data?.success) {
                    setStats(response.data.data);
                } else {
                    setStats({
                        totalStations: 0,
                        totalForms: 0,
                        approvedForms: 0,
                        pendingForms: 0,
                        draftForms: 0
                    });
                }
            } catch (error) {
                console.error('Error fetching user stats:', error);
                setStats({
                    totalStations: 0,
                    totalForms: 0,
                    approvedForms: 0,
                    pendingForms: 0,
                    draftForms: 0
                });
            } finally {
                setLoadingStats(false);
            }
        };

        getStats();
    }, []);

    const handleForgotPassword = async () => {
        if (!user?.email) {
            toast.error('User email address is missing.');
            return;
        }

        setResetLoading(true);
        try {
            await authApi.forgotPassword({ email: user.email });
            toast.success(`A password reset link has been successfully sent to ${user.email}`);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to request password reset. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const getRoleBadgeVariant = (role) => {
        const r = (role || '').toLowerCase();
        if (r === 'admin') return 'danger';
        if (r === 'manager') return 'warning';
        if (r === 'engineer') return 'primary';
        if (r === 'quality') return 'info';
        return 'secondary';
    };

    return (
        <div className="profile-page">
            <div className="page-body py-4">
                <div className="container-xl">
                    <Row className="g-4">
                        {/* Profile Info Card */}
                        <Col lg={4}>
                            <Card className="glass-panel border-0 shadow-sm overflow-hidden h-100">
                                <div className="card-gradient-header" style={{ height: '100px', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}></div>
                                <Card.Body className="text-center pt-0 position-relative">
                                    <div className="profile-avatar-wrapper" style={{ marginTop: '-60px', marginBottom: '20px' }}>
                                        <div className="d-inline-block p-1 bg-white rounded-circle shadow-sm position-relative">
                                            <Avatar 
                                                className="avatar avatar-xl border border-3 border-white" 
                                                role={user?.role} 
                                                name={`${user?.name || ''} ${user?.lastName || ''}`} 
                                                src={user?.profileImage}
                                                style={{ width: '110px', height: '110px', fontSize: '2.2rem', cursor: 'pointer' }}
                                            />
                                            <button
                                                className="avatar-camera-btn border-0"
                                                onClick={() => setShowAvatarModal(true)}
                                                title="Choose Profile Picture or Memoji"
                                                disabled={uploadingImage}
                                            >
                                                {uploadingImage ? <Spinner animation="border" size="sm" /> : <IconCamera size={18} />}
                                            </button>
                                        </div>
                                        <div className="mt-2">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-primary p-0 text-decoration-none fw-semibold me-2"
                                                style={{ fontSize: '12px' }}
                                                onClick={() => setShowAvatarModal(true)}
                                            >
                                                <IconMoodSmile size={14} className="me-1" /> Choose Memoji / Photo
                                            </Button>
                                            {user?.profileImage && (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-danger p-0 text-decoration-none fw-semibold"
                                                    style={{ fontSize: '12px' }}
                                                    onClick={handleRemoveProfileImage}
                                                    disabled={uploadingImage}
                                                >
                                                    <IconTrash size={14} className="me-1" /> Remove
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <h3 className="fw-bold mb-1">{user?.name} {user?.lastName || ''}</h3>
                                    <Badge bg={getRoleBadgeVariant(user?.role)} className="px-3 py-2 text-uppercase mb-4 shadow-sm" style={{ letterSpacing: '0.5px' }}>
                                        <IconShield size={14} className="me-1 align-middle" />
                                        {user?.role}
                                    </Badge>

                                    <div className="profile-details-list text-start mt-2 px-2">
                                        <div className="d-flex align-items-center gap-3 py-3 border-bottom">
                                            <div className="details-icon bg-primary-lt">
                                                <IconMail size={20} className="text-primary" />
                                            </div>
                                            <div className="overflow-hidden">
                                                <small className="text-muted d-block">Email Address</small>
                                                <span className="fw-semibold text-truncate d-block">{user?.email || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-3 py-3 border-bottom">
                                            <div className="details-icon bg-success-lt">
                                                <IconPhone size={20} className="text-success" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Phone Number</small>
                                                <span className="fw-semibold">{user?.phone || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-3 py-3">
                                            <div className="details-icon bg-info-lt">
                                                <IconCalendar size={20} className="text-info" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Date Joined</small>
                                                <span className="fw-semibold">{formatDate(user?.createdAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Stats & Actions Area */}
                        <Col lg={8}>
                            <div className="d-flex flex-column gap-4 h-100">
                                {/* Statistics Section */}
                                <Card className="glass-panel border-0 shadow-sm flex-grow-1">
                                    <Card.Header className="bg-transparent border-0 pt-4 pb-0">
                                        <h4 className="fw-bold m-0 d-flex align-items-center gap-2">
                                            <IconFileAnalytics className="text-primary" size={24} />
                                            Workspace Statistics
                                        </h4>
                                    </Card.Header>
                                    <Card.Body className="d-flex align-items-center">
                                        {loadingStats ? (
                                            <div className="w-100 text-center py-5">
                                                <Spinner animation="border" variant="primary" />
                                                <p className="text-muted mt-2 mb-0">Gathering workspace statistics...</p>
                                            </div>
                                        ) : (
                                            <Row className="g-3 w-100 m-0">
                                                {/* Total Stations */}
                                                <Col md={6}>
                                                    <div className="stats-card p-4 rounded-4 bg-white border shadow-sm h-100 position-relative overflow-hidden">
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <span className="text-muted fw-medium small">Total Stations</span>
                                                            <div className="stats-icon-wrap bg-primary-lt">
                                                                <IconBuildingStore size={22} className="text-primary" />
                                                            </div>
                                                        </div>
                                                        <h2 className="fw-bold mb-1">{stats?.totalStations || 0}</h2>
                                                        <small className="text-muted">Unique assigned stations</small>
                                                    </div>
                                                </Col>

                                                {/* Total Forms */}
                                                <Col md={6}>
                                                    <div className="stats-card p-4 rounded-4 bg-white border shadow-sm h-100 position-relative overflow-hidden">
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <span className="text-muted fw-medium small">Commissioning Forms</span>
                                                            <div className="stats-icon-wrap bg-info-lt">
                                                                <IconFileAnalytics size={22} className="text-info" />
                                                            </div>
                                                        </div>
                                                        <h2 className="fw-bold mb-1">{stats?.totalForms || 0}</h2>
                                                        <small className="text-muted">Total checklists filled</small>
                                                    </div>
                                                </Col>

                                                {/* Breakdown list */}
                                                <Col xs={12} className="mt-4">
                                                    <h5 className="fw-bold mb-3 small text-muted text-uppercase" style={{ letterSpacing: '0.5px' }}>Form Progress Breakdown</h5>
                                                    <Row className="g-3">
                                                        {/* Approved */}
                                                        <Col sm={4}>
                                                            <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 border">
                                                                <IconCircleCheck size={32} className="text-success" />
                                                                <div>
                                                                    <span className="fw-bold d-block h4 m-0">{stats?.approvedForms || 0}</span>
                                                                    <span className="text-muted small">Approved</span>
                                                                </div>
                                                            </div>
                                                        </Col>

                                                        {/* Pending */}
                                                        <Col sm={4}>
                                                            <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 border">
                                                                <IconClock size={32} className="text-warning" />
                                                                <div>
                                                                    <span className="fw-bold d-block h4 m-0">{stats?.pendingForms || 0}</span>
                                                                    <span className="text-muted small">Under Review</span>
                                                                </div>
                                                            </div>
                                                        </Col>

                                                        {/* Draft */}
                                                        <Col sm={4}>
                                                            <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 border">
                                                                <IconAlertCircle size={32} className="text-secondary" />
                                                                <div>
                                                                    <span className="fw-bold d-block h4 m-0">{stats?.draftForms || 0}</span>
                                                                    <span className="text-muted small">Draft Mode</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )}
                                    </Card.Body>
                                </Card>

                                {/* Security / Action Card */}
                                <Card className="glass-panel border-0 shadow-sm">
                                    <Card.Header className="bg-transparent border-0 pt-4 pb-0">
                                        <h4 className="fw-bold m-0 d-flex align-items-center gap-2">
                                            <IconLock className="text-danger" size={24} />
                                            Account Security
                                        </h4>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div>
                                                <p className="mb-1 fw-medium">Need to update or forgot your password?</p>
                                                <small className="text-muted d-block">We will send a highly secure reset link to your registered email address.</small>
                                            </div>
                                            <Button 
                                                variant="danger" 
                                                className="premium-btn shadow-sm px-4 py-2 border-0 fw-bold d-flex align-items-center gap-2"
                                                onClick={handleForgotPassword}
                                                disabled={resetLoading}
                                                style={{ background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' }}
                                            >
                                                {resetLoading ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <IconLock size={18} /> Forgot Password
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <AvatarPickerModal
                show={showAvatarModal}
                onHide={() => setShowAvatarModal(false)}
                currentImage={user?.profileImage}
                userName={`${user?.name || ''} ${user?.lastName || ''}`}
                userRole={user?.role}
                onSelectAvatar={handleSelectAvatarUrl}
                onRemoveAvatar={handleRemoveProfileImage}
                isLoading={uploadingImage}
            />
        </div>
    );
};

export default Profile;
